import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, Sel, opts } from "@/components/kit";
import { api, fmtErr, fileToDataUrl, label } from "@/lib/api";

export const REASONS = ["barrier_malfunction", "emergency", "payment_gateway_issue", "lost_ticket", "vehicle_not_recognized", "vip_authorization", "customer_service_resolution", "power_network_interruption", "other"];
const SENSITIVE = ["lost_ticket", "vip_authorization", "customer_service_resolution", "payment_gateway_issue", "vehicle_not_recognized", "other"];

export default function OverrideDialog({ open, onOpenChange, barrierId, plate, sessionId, onDone }) {
  const [f, setF] = useState({ reason: "", comments: "", plate: "" });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.reason) return toast.error("Reason is mandatory");
    setBusy(true);
    try {
      const { data } = await api.post("/overrides", { barrier_id: barrierId, reason: f.reason, comments: f.comments, plate: f.plate || plate || null, session_id: sessionId || null, photo });
      if (data.status === "pending_approval") toast.warning(`${data.override_no} sent for Branch Manager approval`);
      else if (data.status === "executed") toast.success(`${data.override_no} executed — barrier ${data.barrier_response}`);
      else toast.error(`${data.override_no}: ${label(data.status)} (${data.barrier_response})`);
      onOpenChange(false);
      setF({ reason: "", comments: "", plate: "" });
      onDone?.(data);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card" data-testid="override-dialog">
        <DialogHeader><DialogTitle>Manual barrier override</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Every manual opening is recorded with user, time, lane, plate, session, reason and approver.</p>
        <Field label="Reason (mandatory)"><Sel value={f.reason} onChange={(v) => setF({ ...f, reason: v })} options={opts(REASONS)} testid="override-reason" className="w-full" /></Field>
        {SENSITIVE.includes(f.reason) && <div className="rounded bg-amber-500/10 px-3 py-2 text-xs text-amber-500">Sensitive reason — requires Branch Manager approval unless you are a Branch Manager / Super Admin.</div>}
        <Field label="Vehicle plate"><Input data-testid="override-plate" className="font-mono uppercase" value={f.plate || plate || ""} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} /></Field>
        <Field label="Comments"><Textarea data-testid="override-comments" value={f.comments} onChange={(e) => setF({ ...f, comments: e.target.value })} /></Field>
        <Field label="Photo (optional)"><Input type="file" accept="image/*" capture="environment" data-testid="override-photo" onChange={async (e) => e.target.files[0] && setPhoto(await fileToDataUrl(e.target.files[0]))} /></Field>
        <Button data-testid="override-submit" onClick={submit} disabled={busy} className="bg-red-600 font-bold hover:bg-red-500">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit override</Button>
      </DialogContent>
    </Dialog>
  );
}
